// Subscription Service for Trust Tech Studio
const STORAGE_KEY = 'trust-tech-studio-subscriptions';

// Subscription tiers with features
export const SUBSCRIPTION_TIERS = {
  FREE: {
    id: 'free',
    name: 'Free Trial',
    price: 0,
    billingCycle: 'monthly',
    features: [
      'Basic Trust Score Analysis',
      'Up to 5 scans per month',
      'Limited dashboard access',
      'Community support',
      '7-day data retention'
    ],
    limits: {
      scansPerMonth: 5,
      users: 1,
      dataRetentionDays: 7,
      advancedAnalytics: false,
      apiAccess: false,
      customReports: false
    }
  },
  STARTER: {
    id: 'starter',
    name: 'Starter',
    price: 99,
    billingCycle: 'monthly',
    features: [
      'Everything in Free',
      'Up to 50 scans per month',
      'Full dashboard access',
      'Email support',
      '30-day data retention',
      'Basic analytics'
    ],
    limits: {
      scansPerMonth: 50,
      users: 3,
      dataRetentionDays: 30,
      advancedAnalytics: false,
      apiAccess: false,
      customReports: true
    }
  },
  PROFESSIONAL: {
    id: 'professional',
    name: 'Professional',
    price: 299,
    billingCycle: 'monthly',
    features: [
      'Everything in Starter',
      'Unlimited scans',
      'Advanced analytics & AI insights',
      'Priority support',
      '90-day data retention',
      'API access',
      'Custom reports',
      'Supplier ecosystem monitoring',
      'Compliance tools'
    ],
    limits: {
      scansPerMonth: -1, // unlimited
      users: 10,
      dataRetentionDays: 90,
      advancedAnalytics: true,
      apiAccess: true,
      customReports: true
    }
  },
  ENTERPRISE: {
    id: 'enterprise',
    name: 'Enterprise',
    price: 999,
    billingCycle: 'monthly',
    features: [
      'Everything in Professional',
      'Unlimited users',
      'Unlimited data retention',
      'Dedicated account manager',
      '24/7 phone support',
      'Custom integrations',
      'White-label options',
      'SLA guarantee',
      'Advanced security features',
      'On-premise deployment option'
    ],
    limits: {
      scansPerMonth: -1, // unlimited
      users: -1, // unlimited
      dataRetentionDays: -1, // unlimited
      advancedAnalytics: true,
      apiAccess: true,
      customReports: true
    }
  }
};

// Subscription status enum
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TRIAL: 'trial',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  PENDING: 'pending'
};

// Get current user's subscription from localStorage
export function getCurrentSubscription() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      // Default to free trial
      return {
        tierId: 'free',
        status: SUBSCRIPTION_STATUS.TRIAL,
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days trial
        usage: {
          scansThisMonth: 0,
          lastResetDate: new Date().toISOString()
        }
      };
    }
    return JSON.parse(data);
  } catch (error) {
    console.error('Error loading subscription:', error);
    return null;
  }
}

// Save subscription
export function saveSubscription(subscription) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(subscription));
    return true;
  } catch (error) {
    console.error('Error saving subscription:', error);
    return false;
  }
}

// Check if feature is available in current subscription
export function hasFeature(featureName) {
  const subscription = getCurrentSubscription();
  if (!subscription) return false;
  
  const tier = SUBSCRIPTION_TIERS[subscription.tierId.toUpperCase()];
  if (!tier) return false;
  
  // Check limits object for specific features
  if (tier.limits.hasOwnProperty(featureName)) {
    return tier.limits[featureName];
  }
  
  return false;
}

// Check if user can perform action based on limits
export function canPerformAction(action, count = 1) {
  const subscription = getCurrentSubscription();
  if (!subscription) return false;
  
  const tier = SUBSCRIPTION_TIERS[subscription.tierId.toUpperCase()];
  if (!tier) return false;
  
  // Reset monthly usage if needed
  const lastReset = new Date(subscription.usage.lastResetDate);
  const now = new Date();
  if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) {
    subscription.usage.scansThisMonth = 0;
    subscription.usage.lastResetDate = now.toISOString();
    saveSubscription(subscription);
  }
  
  // Check limits
  switch (action) {
    case 'scan':
      if (tier.limits.scansPerMonth === -1) return true; // unlimited
      return subscription.usage.scansThisMonth + count <= tier.limits.scansPerMonth;
    
    case 'addUser':
      if (tier.limits.users === -1) return true; // unlimited
      return (subscription.usage.activeUsers || 1) + count <= tier.limits.users;
    
    default:
      return true;
  }
}

// Record usage
export function recordUsage(action, count = 1) {
  const subscription = getCurrentSubscription();
  if (!subscription) return false;
  
  switch (action) {
    case 'scan':
      subscription.usage.scansThisMonth = (subscription.usage.scansThisMonth || 0) + count;
      break;
    
    case 'addUser':
      subscription.usage.activeUsers = (subscription.usage.activeUsers || 1) + count;
      break;
  }
  
  return saveSubscription(subscription);
}

// Upgrade subscription
export function upgradeSubscription(newTierId, paymentMethod = null) {
  const subscription = getCurrentSubscription();
  if (!subscription) return { success: false, error: 'No subscription found' };
  
  const newTier = SUBSCRIPTION_TIERS[newTierId.toUpperCase()];
  if (!newTier) return { success: false, error: 'Invalid subscription tier' };
  
  // In production, this would integrate with payment gateway
  subscription.tierId = newTierId;
  subscription.status = SUBSCRIPTION_STATUS.ACTIVE;
  subscription.startDate = new Date().toISOString();
  subscription.endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days
  subscription.paymentMethod = paymentMethod;
  subscription.lastPaymentDate = new Date().toISOString();
  
  if (saveSubscription(subscription)) {
    return { success: true, subscription };
  }
  
  return { success: false, error: 'Failed to save subscription' };
}

// Cancel subscription
export function cancelSubscription() {
  const subscription = getCurrentSubscription();
  if (!subscription) return { success: false, error: 'No subscription found' };
  
  subscription.status = SUBSCRIPTION_STATUS.CANCELLED;
  subscription.cancellationDate = new Date().toISOString();
  
  if (saveSubscription(subscription)) {
    return { success: true, subscription };
  }
  
  return { success: false, error: 'Failed to cancel subscription' };
}

// Get subscription tier details
export function getTierDetails(tierId) {
  return SUBSCRIPTION_TIERS[tierId.toUpperCase()] || null;
}

// Get all available tiers
export function getAllTiers() {
  return Object.values(SUBSCRIPTION_TIERS);
}

// Check if subscription is expired
export function isSubscriptionExpired() {
  const subscription = getCurrentSubscription();
  if (!subscription) return true;
  
  const endDate = new Date(subscription.endDate);
  const now = new Date();
  
  return now > endDate;
}

// Get days remaining
export function getDaysRemaining() {
  const subscription = getCurrentSubscription();
  if (!subscription) return 0;
  
  const endDate = new Date(subscription.endDate);
  const now = new Date();
  const diffTime = endDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
}

// Get usage statistics
export function getUsageStats() {
  const subscription = getCurrentSubscription();
  if (!subscription) return null;
  
  const tier = SUBSCRIPTION_TIERS[subscription.tierId.toUpperCase()];
  if (!tier) return null;
  
  return {
    scans: {
      used: subscription.usage.scansThisMonth || 0,
      limit: tier.limits.scansPerMonth,
      percentage: tier.limits.scansPerMonth === -1 
        ? 0 
        : ((subscription.usage.scansThisMonth || 0) / tier.limits.scansPerMonth) * 100
    },
    users: {
      used: subscription.usage.activeUsers || 1,
      limit: tier.limits.users,
      percentage: tier.limits.users === -1 
        ? 0 
        : ((subscription.usage.activeUsers || 1) / tier.limits.users) * 100
    },
    daysRemaining: getDaysRemaining()
  };
}
